/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { CalendarTask } from '../../model/calendar/calendar-task';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CalendarTaskHeaderGroup } from '../../model/calendar/calendar-task-header-group';
import { EventEmitter } from '@angular/core';
import { CalendarDropTask } from '../../model/calendar/calendar-drop-task';

export class TaskManager {
  set copyTask(value: CalendarTask) {
    this._copyTask = value;
    this.copyTaskEmitter.emit(value);
  }
  set mode(value: 'together' | 'separate') {
    this._mode = value;
    if (value === 'separate' && this.groups) {
      this.generateGroupTask();
    } else if (value === 'together') {
      this.dataSource.next(this.data);
    }
  }
  get mode(): 'together' | 'separate' {
    return this._mode;
  }
  public dataSource = new BehaviorSubject<CalendarTask[]>([]);
  public dataSourceGroup = new BehaviorSubject<CalendarTaskHeaderGroup[]>([]);
  protected scrollToObs = new BehaviorSubject<CalendarTask>(null);
  public data: CalendarTask[] = [];
  protected _mode: 'together' | 'separate' = 'together';
  public groups: CalendarTaskHeaderGroup[];

  protected copyTaskEmitter = new EventEmitter<CalendarTask>();

  protected _copyTask: CalendarTask;

  public draggingTask: CalendarDropTask<CalendarTask>;

  public currentPage = 0;
  public count: number;
  public step: number;

  public scrollToTask: CalendarTask;
  protected scrollToDate$ = new EventEmitter<Date>();

  public separateCalendars(groups: CalendarTaskHeaderGroup[]) {
    this.groups = groups;
    this.mode = 'separate';
  }

  public add(task: CalendarTask) {
    this.data.push(task);
    if (this._mode === 'separate') {
      if (task.group) {
        const header = this.groups.find((gr) => gr.id === task.group.header);
        if (header) {
          header.addTask(task);
        }
      }
      this.dataSourceGroup.next(this.groups);
    } else {
      this.dataSource.next(this.data);
    }
  }

  public setTasks(tasks: CalendarTask[]) {
    this.data = [].concat(tasks);
    if (this._mode === 'together') {
      this.dataSource.next(this.data);
    } else if (this.groups) {
      this.generateGroupTask();
    }
  }

  public remove(task: CalendarTask) {
    const index = this.data.findIndex((t) => t.id === task.id);
    if (index >= 0) {
      this.data.splice(index, 1);
      this.dataSource.next(this.data);
    }
    return index;
  }

  /**
   * clear all tasks
   */
  public clear() {
    this.data = [];
    this.dataSource.next(this.data);
  }

  /**
   * get a copy of current data array
   */
  public getData(): CalendarTask[] {
    return this.data.slice(0);
  }

  public generateGroupTask() {
    this.cleanGroups();
    for (const d of this.data) {
      if (d.group) {
        const header = this.groups.find((gr) => gr.id === d.group.header);
        if (header) {
          header.addTask(d);
        }
      }
    }
    this.dataSourceGroup.next(this.groups.slice(0));
  }

  notify() {
    if (this.mode === 'together') {
      this.dataSource.next(this.data);
    } else {
      this.dataSourceGroup.next(this.groups);
    }
  }

  protected cleanGroups() {
    this.groups.forEach((gr) => {
      gr.totalTasksDuration = 0;
      gr.tasksGroups.forEach((tg) => {
        tg.totalTasksDuration = 0;
        tg.tasks = [];
      });
    });
  }

  changeTaskGroup(task: CalendarTask, header: string, group: string) {
    task.group = { header: header, group: group };
    this.generateGroupTask();
  }

  public getCopyTask(): Observable<CalendarTask> {
    return this.copyTaskEmitter.asObservable();
  }

  setDraggingItem(task: CalendarDropTask<any>) {
    this.draggingTask = task;
  }

  next() {
    const length = Math.ceil(this.count / this.step);
    if (length > this.currentPage) {
      this.currentPage++;
    }
  }

  previous() {
    if (0 < this.currentPage) {
      this.currentPage--;
    }
  }

  changeIndex(index: number) {
    this.currentPage = index;
  }

  public scrollTo(task: CalendarTask) {
    this.scrollToObs.next(task);
  }

  public scrollToDate(date: Date) {
    this.scrollToDate$.emit(date);
  }

  public subscribeScrollDate(next: (date: Date) => void): Subscription {
    return this.scrollToDate$.subscribe(next);
  }

  public getScrollTo(): Observable<CalendarTask> {
    return this.scrollToObs.asObservable();
  }
}
